import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';

/**
 * Notificações não-bloqueantes (toasts), no lugar dos modais Swal.
 * API mantida (`showError`/`showSuccess`) — nenhuma tela que usa o hook precisa
 * mudar. Confirmações críticas continuam em diálogo próprio (não passam por aqui).
 */
const NotificationContext = createContext(null);
const AUTODISMISS = 5000;
let seq = 0;

// Ponte imperativa para componentes de CLASSE (não podem usar o hook).
// O provider registra seu `push` aqui ao montar; `notify.*` chama esse push.
let externalPush = null;

export function NotificationProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const remove = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const push = useCallback(
    (type, message, title) => {
      const id = ++seq;
      // Erros ficam até o usuário fechar (mensagens fiscais são longas/críticas);
      // sucesso/info somem sozinhos.
      const autodismiss = type !== 'error';
      // mantém no máximo 5 toasts na tela (descarta o mais antigo)
      setToasts((list) => [...list.slice(-4), { id, type, title, message, autodismiss }]);
      if (autodismiss) timers.current[id] = setTimeout(() => remove(id), AUTODISMISS);
    },
    [remove]
  );

  const showError = useCallback(
    (message, title = 'Erro') => push('error', message || 'Ocorreu um erro inesperado.', title),
    [push]
  );
  const showSuccess = useCallback(
    (message, title = 'Sucesso') => push('success', message, title),
    [push]
  );
  // Neutro/informativo (ex.: "este cupom não tem documento fiscal"): não é erro
  // nem sucesso — some sozinho como o sucesso.
  const showInfo = useCallback(
    (message, title = 'Informação') => push('info', message, title),
    [push]
  );

  // Registra o push para a ponte imperativa (notify.*) usada por classes.
  useEffect(() => {
    externalPush = push;
    return () => {
      if (externalPush === push) externalPush = null;
    };
  }, [push]);

  useEffect(() => {
    const store = timers.current;
    return () => Object.values(store).forEach(clearTimeout);
  }, []);

  const value = useMemo(
    () => ({ showError, showSuccess, showInfo }),
    [showError, showSuccess, showInfo]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div className="fatura-toasts" role="region" aria-live="polite" aria-label="Notificações">
            {toasts.map((t) => (
              <div
                key={t.id}
                className={`fatura-toast fatura-toast--${
                  t.type === 'error' ? 'err' : t.type === 'info' ? 'info' : 'ok'
                }`}
                role="status"
              >
                <span className="fatura-toast__ic">
                  {t.type === 'success' ? <CheckCircle2 size={17} /> : <AlertCircle size={17} />}
                </span>
                <span className="fatura-toast__body">
                  {t.title ? <span className="fatura-toast__title">{t.title}</span> : null}
                  {t.message ? <span className="fatura-toast__msg">{t.message}</span> : null}
                </span>
                <button
                  type="button"
                  className="fatura-toast__close"
                  onClick={() => remove(t.id)}
                  aria-label="Fechar notificação"
                >
                  <X size={15} />
                </button>
                {t.autodismiss ? <span className="fatura-toast__bar" /> : null}
              </div>
            ))}
          </div>,
          document.body
        )}
    </NotificationContext.Provider>
  );
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification deve ser usado dentro de NotificationProvider');
  }
  return context;
}

/**
 * API imperativa para componentes de classe (ou fora de React):
 *   import { notify } from "components/Notification";
 *   notify.error("mensagem"); notify.success("mensagem");
 * Usa o mesmo sistema de toast (não-bloqueante) do NotificationProvider.
 */
export const notify = {
  error: (message, title = 'Erro') =>
    externalPush && externalPush('error', message || 'Ocorreu um erro inesperado.', title),
  success: (message, title = 'Sucesso') => externalPush && externalPush('success', message, title),
  info: (message, title = 'Informação') => externalPush && externalPush('info', message, title),
};
